import React from 'react'

import {
  Breakpoint,
  Component,
  Part,
  allParts,
} from '@/components/ui/details/details.tsx'

function ComponentBuilder<T>(component: Component<T>) {
  return {
    ...component,
    withName(name: string) {
      this.name = name

      return this
    },
    withOrder(part: Part, order: number) {
      if (!this.order) {
        this.order = {}
      }
      this.order[part] = order

      return this
    },
    addPositions(part: Part, position: Breakpoint[]) {
      if (!this.positions) {
        this.positions = {}
      }
      this.positions[part] = position

      return this
    },
    withClassName(className: string) {
      if (!this.classNames) {
        this.classNames = {}
      }
      for (const part of allParts) {
        this.classNames[part] = className
      }

      return this
    },
    addClassName(part: Part, className: string) {
      if (!this.classNames) {
        this.classNames = {}
      }
      this.classNames[part] = className

      return this
    },
    withRender(render: (value: T) => React.ReactNode) {
      this.render = render

      return this
    },
  }
}

interface Options {
  name?: string
  positions?: Breakpoint[]
  order?: number
  className?: string
}

export function createDetailsBuilder<T>() {
  return {
    options: (
      order: number,
      name: string,
      className: string,
      positions: Breakpoint[],
    ) => ({
      name,
      order,
      className,
      positions,
    }),
    named: (name: string) =>
      ComponentBuilder<T>({
        classNames: {},
        render: () => null,
        name,
        order: {},
        positions: {},
      }),
    title: (options: Options, render: (value: T) => React.ReactNode) =>
      ComponentBuilder<T>({
        classNames: {
          title: options.className ?? '',
        },
        render,
        name: options.name ?? 'title-' + options.order ?? 0,
        order: { title: options.order ?? 0 },
        positions: { title: options.positions ?? ['xs'] },
      }),
    detail: (name: string, render: (value: T) => React.ReactNode) =>
      ComponentBuilder<T>({
        classNames: {
          details: 'block col-span-6 sm:col-span-12',
        },
        render,
        name,
        order: { details: 0 },
        positions: { details: ['xs'] },
      }),
    autoDetail: function <Name extends keyof T>(
      name: Name,
      displayName: string,
    ) {
      return this.detail(
        displayName,
        (value) => value[name as Name] as React.ReactNode,
      )
    },
    autoDetails: function <Name extends keyof T>(...names: Name[]) {
      return names.map((name) =>
        this.autoDetail(name, toTitelCase(name as string)),
      )
    },
    footer: (render: (value: T) => React.ReactNode) =>
      ComponentBuilder<T>({
        classNames: {},
        render,
        name: 'footer',
        order: { footer: 0 },
        positions: { footer: ['xs'] },
      }),
  }
}

function toTitelCase(name: string) {
  return name[0].toUpperCase() + name.slice(1)
}
